import { useEffect, useMemo, useState } from 'react';
import { Building2, Layers, Mail, Shield, UserCircle } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { iamApi } from '@/modules/iam/services/iam.api';
import { getApiErrorMessage } from '@/lib/http';

export function AccountPage() {
  const { user, organization, workspaces, currentWorkspaceId, memberships, init } = useAuth();
  const currentWorkspace = useMemo(
    () => workspaces.find((w) => w._id === currentWorkspaceId) || null,
    [workspaces, currentWorkspaceId],
  );
  const topRole = memberships[0]?.roleName || 'No role';
  const roleScope = memberships[0]?.roleScope || '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
  }, [user?.firstName, user?.lastName]);

  const dirty =
    (user?.firstName || '') !== firstName.trim() || (user?.lastName || '') !== lastName.trim();

  async function handleSave() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      await iamApi.updateMe({ firstName: firstName.trim(), lastName: lastName.trim() });
      await init();
      setSavedAt(Date.now());
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="min-h-full px-8 py-10 max-w-4xl mx-auto"
      style={{ background: 'var(--canvas-base)' }}
    >
      <header className="mb-8">
        <h1
          className="text-[34px] font-light tracking-[-0.02em] leading-tight"
          style={{ fontFamily: 'var(--v2-font-serif)', color: 'var(--text-primary)' }}
        >
          Account
        </h1>
        <p className="text-sm mt-1.5" style={{ color: 'var(--text-secondary)' }}>
          Your profile and where you sit in the organization.
        </p>
      </header>

      {/* Status card */}
      <section
        className="rounded-2xl p-6 mb-8"
        style={{
          background: 'var(--surface-primary)',
          border: '1px solid var(--border-default)',
          boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--bg-accent-soft, #eef2ff)', color: 'var(--accent, #4f46e5)' }}
          >
            <UserCircle size={24} strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              {(user?.firstName || user?.lastName)
                ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
                : (user?.email || 'You')}
            </h2>
            <p className="text-sm flex items-center gap-1.5 mt-1" style={{ color: 'var(--text-secondary)' }}>
              <Mail size={12} strokeWidth={1.7} />
              {user?.email}
            </p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatusCell
                icon={Shield}
                label="Role"
                value={topRole}
                hint={roleScope ? `${roleScope} scope` : undefined}
              />
              <StatusCell
                icon={Layers}
                label="Workspace"
                value={currentWorkspace?.name || '—'}
              />
              <StatusCell
                icon={Building2}
                label="Organization"
                value={organization?.name || '—'}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Editable name form */}
      <section
        className="rounded-2xl p-6"
        style={{
          background: 'var(--surface-primary)',
          border: '1px solid var(--border-default)',
        }}
      >
        <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
          Profile
        </h3>
        <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
          Your name appears in invites and on candidate-facing communications.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
              First name
            </span>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{
                background: 'var(--canvas-base)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
              placeholder="First name"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
              Last name
            </span>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{
                background: 'var(--canvas-base)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
              placeholder="Last name"
            />
          </label>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40"
            style={{ background: 'var(--accent, #4f46e5)', color: 'white' }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {error ? (
            <span className="text-sm" style={{ color: 'var(--fg-danger, #b91c1c)' }}>
              {error}
            </span>
          ) : savedAt && !dirty ? (
            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
              Saved
            </span>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function StatusCell({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Building2;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div
      className="rounded-xl p-3"
      style={{ background: 'var(--canvas-base)', border: '1px solid var(--border-muted, var(--border-default))' }}
    >
      <div
        className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider mb-1"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <Icon size={11} strokeWidth={1.7} />
        {label}
      </div>
      <div className="text-sm font-medium leading-tight" style={{ color: 'var(--text-primary)' }}>
        {value}
      </div>
      {hint ? (
        <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}
